import { useEffect, useState, type JSX, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { Menu, X } from 'lucide-react'
import { ToastProvider } from '../system/ToastProvider.js'
import { api } from '../../api/client.js'
import { deriveTenantSlugFromHost } from '../../utils/deriveTenantSlugFromHost.js'
import { useGoogleFont } from '../../hooks/useGoogleFont.js'
import { FontLoadingBadge } from './FontLoadingBadge.js'
import { ChatWidgetRoot, createWidgetSocket } from '@kc3/chat-widget'
import '@kc3/chat-widget/embed.css'
import { isPrivacyNoticeActive } from '@kc3/contracts'
import { ClientChatLauncher } from './ClientChatLauncher.js'
import { ClientVoiceAssistant } from './voice/ClientVoiceAssistant.js'
import {
  HomeSection,
  FooterSection,
  themeAccent,
  visibleSorted,
} from '../../pages/portal/PortalPage.js'
import type { PortalConfigData, PortalTemplateLike, LivePortalPayload, PortalTemplateSection } from '../../pages/portal/PortalPage.js'

interface PortalLayoutProps {
  children: ReactNode | ((ctx: { config: PortalConfigData; homeSections: PortalTemplateSection[] }) => ReactNode)
  overrideConfig?: PortalConfigData
  overrideTemplates?: { navbar: PortalTemplateLike | null; footer: PortalTemplateLike | null }
  overrideChatSession?: { token: string; visitorId: string } | null
  isPreview?: boolean
}

/* ─── Design Library's preview iframe has no real tenant context (its URL
   is literally /portal/preview), so it never gets an injected chat session
   — this renders a static, inert bubble purely showing placement instead
   of leaving the widget's spot blank. ─── */
function ChatWidgetPreviewInert(): JSX.Element {
  return (
    <button
      type="button"
      aria-label="Chat preview (not interactive)"
      title="Chat preview — not interactive in draft mode"
      disabled
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        border: 'none',
        background: 'var(--portal-accent, #0070f3)',
        color: '#fff',
        fontSize: '24px',
        cursor: 'default',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        zIndex: 1000,
      }}
    >
      💬
    </button>
  )
}

export default function PortalLayout({
  children,
  overrideConfig,
  overrideTemplates,
  overrideChatSession,
  isPreview = false,
}: PortalLayoutProps): JSX.Element {
  const tenantSlug = deriveTenantSlugFromHost(window.location.host) ?? undefined
  const location = useLocation()
  const hideFooter = /^\/client-portal\/(login|register)(\/.*)?$/.test(location.pathname)
  const [fetchedConfig, setFetchedConfig] = useState<PortalConfigData | null>(null)
  const [fetchedNavbar, setFetchedNavbar] = useState<PortalTemplateLike | null>(null)
  const [fetchedFooter, setFetchedFooter] = useState<PortalTemplateLike | null>(null)
  const [fetchedHome, setFetchedHome] = useState<PortalTemplateSection[]>([])
  const [loading, setLoading] = useState(!isPreview)
  const [error, setError] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)

  const currentConfig = isPreview ? overrideConfig : fetchedConfig
  const currentNotice = currentConfig?.privacyNotice
  const currentNoticeActive = isPrivacyNoticeActive(currentNotice)

  useEffect(() => {
    if (currentNoticeActive && currentNotice?.version) {
      try {
        setPrivacyAccepted(localStorage.getItem(`kc3_privacy_accepted_${currentNotice.version}`) === 'true')
      } catch {
        setPrivacyAccepted(false)
      }
    } else {
      setPrivacyAccepted(true)
    }
  }, [currentNoticeActive, currentNotice?.version])

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (isPreview || !tenantSlug) {
      setLoading(false)
      return
    }
    let cancelled = false
    api
      .get<LivePortalPayload>(`/portal-config/live?tenantSlug=${encodeURIComponent(tenantSlug)}`)
      .then((res) => {
        if (cancelled) return
        setFetchedConfig(res.config)
        setFetchedNavbar(res.navbarTemplate ?? null)
        setFetchedFooter(res.footerTemplate ?? null)
        setFetchedHome(res.homeTemplate?.sections ?? [])
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load portal')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [isPreview, tenantSlug])

  const config = isPreview ? overrideConfig : fetchedConfig
  const navbarTemplate = isPreview ? (overrideTemplates?.navbar ?? null) : fetchedNavbar
  const footerTemplate = isPreview ? (overrideTemplates?.footer ?? null) : fetchedFooter

  const { status: fontStatus } = useGoogleFont(config?.design.font || null)

  if (!isPreview && error) {
    return <div className="p-10 text-center text-red-600">{error}</div>
  }

  if (!isPreview && (loading || !config)) {
    return <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
  }

  if (isPreview && !config) {
    return <div className="p-10 text-center text-gray-400 text-sm">Waiting for preview data…</div>
  }

  const resolvedConfig = config as PortalConfigData
  const isSidebarLayout = resolvedConfig.design.layout === 'menu_side'
  const navbarSections = visibleSorted(navbarTemplate?.sections ?? [])
  const rootStyle = {
    fontFamily: resolvedConfig.design.font
      ? `"${resolvedConfig.design.font}", Inter, system-ui, -apple-system, sans-serif`
      : undefined,
    ['--portal-accent' as string]: themeAccent(resolvedConfig.design.theme),
    ['--portal-accent-text' as string]: resolvedConfig.design.buttonTextColor || '#ffffff',
  }

  const chatSession = isPreview ? overrideChatSession : undefined
  const chatWidget = resolvedConfig.accessPermissions.chat.enabled && (
    isPreview
      ? (chatSession
          ? <ChatWidgetRoot
              socket={createWidgetSocket(chatSession.token)}
              visitorId={chatSession.visitorId}
              token={chatSession.token}
              {...(isPrivacyNoticeActive(resolvedConfig.privacyNotice) && {
                // PV-16: preview shows the draft notice; the server does not record acceptance for the preview token.
                privacyNotice: {
                  url: resolvedConfig.privacyNotice.noticeUrl,
                  text: resolvedConfig.privacyNotice.noticeText,
                  version: resolvedConfig.privacyNotice.version,
                },
              })}
            />
          : <ChatWidgetPreviewInert />)
      : (tenantSlug && <ClientChatLauncher tenantSlug={tenantSlug} />)
  )

  const resolvedChildren = typeof children === 'function'
    ? children({ config: resolvedConfig, homeSections: fetchedHome })
    : children

  const navbar = (
    <>
      {navbarSections.map((section) => (
        <HomeSection key={`${section.sectionType}-${section.order}`} section={section} config={resolvedConfig} {...(tenantSlug !== undefined && { tenantSlug })} isPreview={isPreview} inNavbar />
      ))}
    </>
  )

  const footerSections = visibleSorted(footerTemplate?.sections ?? [])
  const hasFullWidthFooterSection = footerSections.some(
    (section) => section.sectionType === 'custom_html' && (section.settings.fullWidth as boolean | undefined) === true,
  )

  const privacyNotice = resolvedConfig.privacyNotice
  const isNoticeActive = isPrivacyNoticeActive(privacyNotice)

  const handleAcknowledgePrivacy = () => {
    if (privacyNotice?.version) {
      try {
        localStorage.setItem(`kc3_privacy_accepted_${privacyNotice.version}`, 'true')
      } catch {
        // ignore
      }
      // PV-16: also record the acceptance on the server (pseudonymous browser id + notice version).
      // Best effort: failing to record must not lock the visitor out.
      if (tenantSlug) {
        let visitorId = ''
        try {
          visitorId = localStorage.getItem('kc3_portal_visitor') ?? ''
          if (!visitorId) {
            visitorId = crypto.randomUUID()
            localStorage.setItem('kc3_portal_visitor', visitorId)
          }
        } catch {
          visitorId = ''
        }
        if (visitorId) {
          void api
            .post('/widget/v1/portal-privacy-acceptance', { tenantSlug, visitorId, version: privacyNotice.version })
            .catch(() => undefined)
        }
      }
    }
    setPrivacyAccepted(true)
  }

  const footer = (
    <footer style={{ backgroundColor: resolvedConfig.design.footerBackgroundColor || undefined }}>
      <div className={hasFullWidthFooterSection ? undefined : 'max-w-7xl mx-auto px-4 sm:px-6'}>
        {footerSections.map((section) => (
          <FooterSection
            key={`${section.sectionType}-${section.order}`}
            section={section}
            textColor={resolvedConfig.design.footerTextColor || 'color-mix(in srgb, var(--portal-accent) 70%, black)'}
            tenantSlug={tenantSlug ?? ''}
            design={{
              ...resolvedConfig.design,
              privacyUrl: isNoticeActive ? (privacyNotice?.noticeUrl ?? '') : '',
            }}
          />
        ))}
        {footerSections.length === 0 && (
          <div className="text-center text-[13px] text-gray-400 py-4">
            &copy; {new Date().getFullYear()}
            {isNoticeActive && privacyNotice?.noticeUrl && (
              <>
                {' '}&bull;{' '}
                <a href={privacyNotice.noticeUrl} className="hover:underline">
                  Privacy Notice
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </footer>
  )

  const body = isSidebarLayout && navbarSections.length > 0 ? (
    <div className="kc3-portal flex min-h-screen" style={rootStyle}>
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open navigation menu"
        className="fixed left-4 top-4 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#16273F] shadow-md lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 h-screen w-64 shrink-0 overflow-y-auto bg-white transition-transform duration-200 lg:sticky lg:top-0 lg:translate-x-0 lg:bg-transparent ${
          sidebarOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full'
        }`}
      >
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close navigation menu"
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 lg:hidden"
        >
          <X className="h-4 w-4" />
        </button>
        {navbar}
      </aside>
      <div className="flex flex-1 min-w-0 flex-col">
        <main className="flex-1 py-10">{resolvedChildren}</main>
        {!hideFooter && footer}
      </div>
    </div>
  ) : (
    <div className="kc3-portal flex min-h-screen flex-col" style={rootStyle}>
      {navbar}
      <main className="flex-1">{resolvedChildren}</main>
      {!hideFooter && footer}
    </div>
  )

  return (
    <ToastProvider>
      {body}
      {chatWidget}
      {!isPreview && isNoticeActive && !privacyAccepted && privacyNotice && (
        <div className="fixed inset-0 z-40 bg-slate-900/50" aria-hidden="true" data-testid="privacy-gate" />
      )}
      {!isPreview && isNoticeActive && !privacyAccepted && privacyNotice && (
        <aside
          role="region"
          aria-label="Privacy Notice"
          className="fixed bottom-0 inset-x-0 z-50 bg-slate-900/95 backdrop-blur-sm text-white px-4 py-3 sm:px-6 shadow-2xl border-t border-slate-700/80"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm">
            <div className="flex-1 text-slate-200">
              {privacyNotice.noticeText}{' '}
              {privacyNotice.noticeUrl && (
                <a
                  href={privacyNotice.noticeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-400 hover:text-blue-300 underline underline-offset-2 ml-1"
                >
                  Privacy Notice
                </a>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleAcknowledgePrivacy}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-colors shadow-sm"
              >
                Acknowledge &amp; Accept
              </button>
            </div>
          </div>
        </aside>
      )}
      {!isPreview && tenantSlug && <ClientVoiceAssistant tenantSlug={tenantSlug} />}
      {fontStatus === 'loading' && <FontLoadingBadge />}
    </ToastProvider>
  )
}
